<template>
  <div class="bonus-program-details">
    <breadcrumbs :breadcrumbsData="breadcrumbsData" #icon>
      <svg class="icon" aria-hidden="true">
        <use xlink:href="#icon-CC" fill="#fff"></use>
      </svg>
    </breadcrumbs>

    <a-spin :spinning="detailLoading">
      <div class="view-content">
        <div class="tabs-wrap">
          <artmisads-segmented-tabs class="tabs-wrap-tabs" v-model="activeTab" :items="tabItems" />
        </div>

        <div class="metric-cards">
          <div class="metric-card">
            <div class="metric-label">Sales</div>
            <div class="metric-value">{{ currencySymbol }} {{ formatMoney(detailSales) }}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Bonus</div>
            <div class="metric-value">{{ currencySymbol }} {{ formatMoney(detailBonus) }}</div>
          </div>
        </div>

        <!-- Program Info -->
        <div v-show="activeTab === 'info'" class="tab-panel">
          <div class="info-panel">
            <div class="info-row">
              <span class="info-label">Title</span>
              <span class="info-value">{{ detail.title || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Brand</span>
              <span class="info-value">{{ detail.brandName || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">BonusRule</span>
              <span class="info-value">
                <template v-if="isTierRule">
                  <a class="linkStyle" href="#" @click.prevent="openBonusRule">Tier Rates</a>
                </template>
                <template v-else>{{ bonusRuleText }}</template>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">Period</span>
              <span class="info-value">{{ periodText }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Status</span>
              <span class="info-value">
                <Status v-if="detail.status" :type="programStatusType(detail.status)" :text="detail.status"
                  :noBorder="true" />
                <template v-else>-</template>
              </span>
            </div>
          </div>
        </div>

        <!-- Campaigns -->
        <div v-show="activeTab === 'campaigns'" class="tab-panel tab-panel-table">
          <artmisads-table v-model:pagination="campaignPagination" :columns="campaignColumns"
            :data-source="campaignList" :change="onCampaignTableChange" :loading="campaignLoad" :scroll="{ x: 1800 }">
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'campaignStatus'">
                <Status :type="campaignStatusType(record.campaignStatus)"
                  :text="campaignStatusText(record.campaignStatus)" />
              </template>
              <template v-else-if="column.key === 'dateRange'">
                {{ formatDate(record.startDateTimeInEpochMills ?? record.startDate) }} -
                {{ formatDate(record.scheduledEndDateTimeInEpochMills ?? record.endDate) }}
              </template>
              <template v-else-if="
                column.key === 'totalBudget' ||
                column.key === 'budget' ||
                column.key === 'budgetRemaining'
                ||column.key === 'salesAmount'
              ">
                {{ currencySymbol }} {{ formatMoney(record[column.key]) }}
              </template>
              <template v-else-if="column.key === 'productsCount'">
                <a class="linkStyle " href="#" @click.prevent="openProducts(record)">
                  {{ record.productsCount }}
                </a>
              </template>
              <template v-else-if="column.key === 'acceptStatus'">
                <div class="accept-cell">
                  <template v-if="isNotAccepted(record.acceptStatus)">
                    <!-- @click.prevent="onNotAccepted(record)" -->
                    <a class="linkStyle not-accepted-link" href="#" >
                      Not Accepted
                    </a>
                  </template>
                  <template v-else>
                    <Status :type="acceptStatusType()" :text="formatAcceptStatus(record.acceptStatus)"
                      :noBorder="true" />
                    <a v-if="record.campaignUrl" :href="record.campaignUrl" target="_blank" rel="noopener noreferrer"
                      class="external-link">
                      <Icon name="lianjie" class="link-icon" />
                    </a>
                  </template>
                </div>
              </template>
              <template v-else-if="column.key === 'action'">
                <div class="button-box">
                  <artmisads-select-button type="primary" size="small" @click.prevent="openProducts(record)">
                    view product
                  </artmisads-select-button>
                  <artmisads-select-button  size="small" @click.prevent="onNotAccepted(record)">
                    Accept In Amazon
                  </artmisads-select-button>
                </div>
              </template>
            </template>
          </artmisads-table>
        </div>

        <!-- Products -->
        <div v-show="activeTab === 'products'" class="tab-panel tab-panel-table">
          <div class="info-panel">
            <div class="info-row">
              <span class="info-label">Title</span>
              <span class="info-value">{{ detail.title || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Brand</span>
              <span class="info-value">{{ detail.brandName || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">BonusRule</span>
              <span class="info-value">
                <template v-if="isTierRule">
                  <a class="linkStyle" href="#" @click.prevent="openBonusRule">Tier Rates</a>
                </template>
                <template v-else>{{ bonusRuleText }}</template>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">Period</span>
              <span class="info-value">{{ periodText }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Status</span>
              <span class="info-value">
                <Status v-if="detail.status" :type="programStatusType(detail.status)" :text="detail.status"
                  :noBorder="true" />
                <template v-else>-</template>
              </span>
            </div>
          </div>
          <div class="products-toolbar">
            <div class="products-toolbar-top">
              <ButtonGroups :pagination="productPagination" :tableData="productList" @pageChange="onProductPageChange" />
            <a-input-search v-model:value="productFilter.keyword" class="products-search"
                placeholder="Search ASIN/Brand..." allow-clear @search="onProductSearch"
                @pressEnter="onProductSearch" />
            </div>
            
            <div class="products-toolbar-right">
             
              <div class="mid-filter">
                <div class="mid-filter-item">
                  <div class="mid-filter-title">Deals</div>
                  <div class="mid-filter-checkbox">
                    <a-checkbox v-model:checked="productFilter.activeDeals" @change="onProductFilterChange">
                      Active
                    </a-checkbox>
                  </div>
                </div>
                <div class="mid-filter-item">
                  <div class="mid-filter-title">Availability</div>
                  <div class="mid-filter-checkbox">
                    <a-checkbox v-model:checked="productFilter.inStockOnly" @change="onProductFilterChange">
                      In Stock
                    </a-checkbox>
                  </div>
                </div>
                <div class="mid-filter-item" ref="productSortRef">
                  <div class="mid-filter-title">Sort</div>
                  <div >
                    <artmisads-select v-model:value="productFilter.sort" style="width: 160px"
                    :getPopupContainer="() => productSortRef" @change="onProductFilterChange">
                    <a-select-option value="bestSellersAsc">
                      <Icon name="paixu1" class="iconAsc paixuIcon" />
                      Best Sellers
                    </a-select-option>
                    <a-select-option value="bestSellersDesc">
                      <Icon name="paixu1" />
                      Best Sellers
                    </a-select-option>
                    <a-select-option value="priceAsc">
                      <Icon name="paixu1" class="iconAsc paixuIcon" />
                      Price
                    </a-select-option>
                    <a-select-option value="priceDesc">
                      <Icon name="paixu1" />
                      Price
                    </a-select-option>
                    <a-select-option value="newlyAddedAsc">
                      <Icon name="paixu1" class="iconAsc paixuIcon" />
                      Newly Added
                    </a-select-option>
                    <a-select-option value="newlyAddedDesc">
                      <Icon name="paixu1" />
                      Newly Added
                    </a-select-option>
                  </artmisads-select>
                  </div>
                 
                </div>
              </div>
              
            </div>
          </div>

          <artmisads-table v-model:pagination="productPagination" :columns="productColumns" :data-source="productList"
            :change="onProductTableChange" :loading="productLoad" :scroll="{ x: 1800 }">
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'product'">
                <a class="product-cell" :href="getProductDetailHref(record)" target="_blank" rel="noopener noreferrer">
                  <div class="product-img-wrap">
                    <img v-if="record.imageUrl" :src="record.imageUrl" :alt="record.title" class="product-img" />
                  </div>
                  <div class="product-text">
                    <span class="product-title">{{ record.productTitle || '-' }}</span>
                    <span class="product-asin">{{ record.asin || record.productAsin || '-' }}</span>
                  </div>
                </a>
              </template>
              <template v-else-if="column.key === 'campaignAcceptStatus'">
                <Status :type="campaignAcceptStatusType(record.campaignAcceptStatus)" :text="formatCampaignAcceptStatus(record.campaignAcceptStatus)"
                      :noBorder="true" />
              </template>
              <template v-else-if="column.key === 'deal'">
                <div class="deal-cell">
                  <product-deals-column v-if="record.productDeals?.length" v-for="item in record.productDeals"
                    :record="item" :key="item.id" />

                  <span v-if="!record.productDeals?.length" class="deal-empty">-</span>
                </div>
              </template>
              <template v-else-if="column.key === 'productPrice'">
                <span v-if="record.productPrice">
                  {{ getProductCurrencySymbol(record) }} {{ formatMoney(record.productPrice) }}
                </span>
                <span v-else>-</span>
              </template>
              <template v-else-if="column.key === 'stock'">
                {{ formatStock(record) }}
              </template>
              <template v-else-if="column.key === 'action'">
                <div class="button-box">
                <artmisads-select-button v-if="record.productId" type="primary" size="small"  @click.prevent="openAmazonProduct(record)">
                  View Amazon Details Page
                </artmisads-select-button>
                <artmisads-select-button v-if="record.jumpUrl" size="small" @click.prevent="accpetAmazonProduct(record)">
                  Accpet Campaign in Amazon
                </artmisads-select-button>
              </div>
              </template>
            </template>
          </artmisads-table>
        </div>
      </div>
    </a-spin>

    <BonusRuleModal ref="bonusRuleModalRef" />
    <StoreIdNoticeModal ref="storeIdNoticeModalRef" />
    <CampaignProductsModal ref="campaignProductsModalRef" />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import moment from 'moment'
import { message } from 'ant-design-vue'
import breadcrumbs from '~/components/breadcrumbs.vue'
import Status from '@/components/Status.vue'
import Icon from '@/components/Icon.vue'
import ButtonGroups from '~/pages/seller/Brands/components/buttonGroups.vue'
import BonusRuleModal from './components/bonusRuleModal.vue'
import StoreIdNoticeModal from './components/storeIdNoticeModal.vue'
import CampaignProductsModal from './components/campaignProductsModal.vue'
import { creatorConnectionsApi,publisherProductApi } from '@/server/index'
import { CURRENCY_MAPPER, CURRENCY_DATA_MAPPER } from '@/contants/common'
import { useUserInfoStore } from '@/storeData'
import ProductDealsColumn from "~/components/productDealsColumn.vue"
const route = useRoute()
const user = useUserInfoStore()
const countryCode = user?.userInfo?.countryCode || 'US'

const programId = computed(() => route.params.id)
const marketplace = computed(
  () =>
    route.query.marketplace ||
    CURRENCY_MAPPER[countryCode]?.host ||
    localStorage.getItem('selectedSiteMarketplace') ||
    'amazon.com',
)

const detailLoading = ref(false)
const detail = ref({})
const activeTab = ref('info')
const bonusRuleModalRef = ref(null)
const storeIdNoticeModalRef = ref(null)
const campaignProductsModalRef = ref(null)

const campaignLoad = ref(false)
const campaignList = ref([])
const campaignPagination = reactive({
  total: 0,
  current: 1,
  size: 10,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
})

const productLoad = ref(false)
const productList = ref([])
const productSortRef = ref(null)
const productFilter = reactive({
  keyword: '',
  activeDeals: false,
  inStockOnly: false,
  sort: 'bestSellersAsc',
})
const productPagination = reactive({
  total: 0,
  current: 1,
  size: 10,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50', '100'],
})

const breadcrumbsData = computed(() => ({
  title: 'Program Details',
  des: '',
  hasTitleIcon: true,
  color: '#9C7AFB',
  links: [
    { name: 'Home', path: '/publisher/dashboard' },
    {
      name: 'Creator Connections',
      path: '/publisher/creatorConnections/bonusPrograms',
    },
    {
      name: 'Bonus Program',
      path: '/publisher/creatorConnections/bonusPrograms',
    },
    {
      name: 'Program Details',
      path: route.fullPath,
    },
  ],
}))

const tabItems = [
  { key: 'info', label: 'Program Info' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'products', label: 'Products' },
]

const currencyCode = computed(
  () => detail.value.currencyCode ?? detail.value.currency ?? 'USD',
)
const currencySymbol = computed(() => CURRENCY_DATA_MAPPER[currencyCode.value] || '$')

const detailSales = computed(
  () => detail.value.salesAmount ?? detail.value.sales ?? 0,
)
const detailBonus = computed(
  () => detail.value.bonusAmount ?? detail.value.estimatedBonus ?? 0,
)

const isTierRule = computed(() => {
  const type = String(detail.value.bonusRuleType || '').toLowerCase()
  return type === 'tier'
})

const bonusRuleText = computed(() => {
  if (detail.value.bonusRuleSummary) return detail.value.bonusRuleSummary
  const rate = detail.value.cpsRate ?? detail.value.fixedCpsRate ?? detail.value.bonusRate
  if (rate != null) return `Fixed Rate : ${rate}%`
  return '—'
})

const periodText = computed(() => {
  const empty = '-'
  const start = formatDate(
    detail.value.startDateTimestamp ??
    detail.value.startDateInEpochMills ??
    detail.value.startDate,
  )
  const end = formatDate(
    detail.value.endDateTimestamp ?? detail.value.endDateInEpochMills ?? detail.value.endDate,
  )
  const hasStart = start !== empty
  const hasEnd = end !== empty
  if (!hasStart && !hasEnd) return empty
  if (hasStart && hasEnd) return `${start} - ${end}`
  if (hasStart) return start
  return end
})

const productColumns = [
  { title: 'Products', dataIndex: 'product', key: 'product', width: 360 },
  { title: 'Brand', dataIndex: 'brandName', key: 'brandName', width: 120 },
  { title: 'Category', dataIndex: 'category', key: 'category', width: 120 },
  { title: 'Campaign', dataIndex: 'campaignName', key: 'campaignName', width: 240 },
  { title: 'CampaignAcceptStatus', dataIndex: 'campaignAcceptStatus', key: 'campaignAcceptStatus', width: 120 },
  { title: 'Deal', dataIndex: 'deal', key: 'deal', width: 90 },
  { title: 'Price', dataIndex: 'productPrice', key: 'productPrice', width: 100 },
  { title: 'Stock', dataIndex: 'stock', key: 'stock', width: 100 },
  {
    title: 'Action',
    dataIndex: 'action',
    key: 'action',
    width: 200,
    fixed: 'right',
  }
]
const openCampaignUrl = (record) => {
  // window.open(record.campaignUrl, '_blank');

  if(record.hasAssociateStoreId){
    if(record.jumpUrl){
      window.open(record.jumpUrl, '_blank');
    }else{
      storeIdNoticeModalRef.value?.show()
    }
  }else{
    storeIdNoticeModalRef.value?.show();
  }
};
const campaignColumns = [
  { title: 'Name', dataIndex: 'campaignName', key: 'campaignName' },
  { title: 'Brand', dataIndex: 'brandName', key: 'brandName' },
  { title: 'Status', dataIndex: 'campaignStatus', key: 'campaignStatus', width: 120 },
  { title: 'Date', dataIndex: 'dateRange', key: 'dateRange', width: 200 },
  { title: 'Budget', dataIndex: 'budget', key: 'budget', width: 200 },
  { title: 'BudgetRemaining', dataIndex: 'budgetRemaining', key: 'budgetRemaining', width: 200 },
  {title:'product',dataIndex:"productsCount",key:"productsCount",width:200},
  {title:'sales',dataIndex:"salesAmount",key:"salesAmount",width:200},

  { title: 'AcceptStatus', dataIndex: 'acceptStatus', key: 'acceptStatus', width: 150 },
  { title: 'Action', dataIndex: 'action', key: 'action', width: 180,fixed: 'right' },
]

const formatMoney = (val) => Number(val ?? 0).toFixed(2)
const formatDate = (ts) => (ts ? moment(ts).format('YYYY-MM-DD') : '-')

const campaignStatusText = (status) => {
  const map = {
    DELIVERING: 'Delivering',
    ENDED: 'Ended',
    EXPIRED: 'Expired',
    OUTOFBUDGET: 'Out of budget',
    CREATED: 'Created',
    DELETED: 'Deleted',
  }
  return map[status] || status
}
const campaignAcceptStatusType = (status) => {
  if (status === 'ACCEPTED' || status === 'accepted') return 'active'
  if (status === 'NOT_ACCEPTED' || status === 'not_accepted' || status === 'NOT ACCEPTED' || status === 'Not Accepted') return 'inactive'
  return 'inactive'
} 
const formatCampaignAcceptStatus = (status) => {
  if (status === 'ACCEPTED' || status === 'accepted') return 'Accepted'
  if (status === 'NOT_ACCEPTED' || status === 'not_accepted' || status === 'NOT ACCEPTED' || status === 'Not Accepted') return 'Not Accepted'
  return status || '—'
}
const campaignStatusType = (status) => {
  if (status === 'CREATED' || status === 'EXPIRED') return 'inactive'
  if (status === 'DELIVERING') return 'delivering'
  if (status === 'ENDED') return 'active'
  if (status === 'OUTOFBUDGET') return 'pending'
  if (status === 'DELETED') return 'not_authorized'
  return 'inactive'
}

const programStatusType = (status) => {
  if (status === 'ACTIVE') return 'active'
  if (status === 'COMPLETED') return 'finished'
  if (status === 'CANCELLED') return 'inactive'
  if (status === 'INCOMING') return 'delivering'
  return 'inactive'
}

const formatAcceptStatus = (status) => {
  if (status === 'ACCEPTED' || status === 'accepted') return 'Accepted'
  return status || '—'
}

const isNotAccepted = (status) =>
  status === 'NOT_ACCEPTED' ||
  status === 'not_accepted' ||
  status === 'NOT ACCEPTED' ||
  status === 'Not Accepted'

const acceptStatusType = () => 'active'

const onNotAccepted = (record) => {
  if(record.hasAssociateStoreId){
    if(record.jumpUrl){
      window.open(record.jumpUrl, '_blank');
    }else{
      storeIdNoticeModalRef.value?.show()
    }
  }else{
    storeIdNoticeModalRef.value?.show();
  }
}

const fetchProgramDetail = async () => {
  if (!programId.value) return
  detailLoading.value = true
  try {
    const res = await creatorConnectionsApi.getBonusProgramDetail({
      programId: programId.value,
      marketplace: marketplace.value,
    })
    if (res?.success && res?.data) {
      detail.value = res.data
    } else {
      message.error(res?.error || 'Failed to load data')
    }
  } catch {
    message.error('Failed to load data')
  } finally {
    detailLoading.value = false
  }
}

const fetchCampaigns = async () => {
  if (!programId.value) return
  campaignLoad.value = true
  try {
    const res = await creatorConnectionsApi.getBonusProgramCampaigns({
      programId: programId.value,
      page: campaignPagination.current,
      size: campaignPagination.size,
      marketplace: marketplace.value,
    })
    if (res?.success && res?.data) {
      const list = res.data.data ?? res.data
      campaignList.value = Array.isArray(list) ? list : []
      campaignPagination.total = res.data.total ?? campaignList.value.length
    } else {
      campaignList.value = []
      campaignPagination.total = 0
      if (res?.error) message.error(res.error)
    }
  } catch {
    campaignList.value = []
    campaignPagination.total = 0
    message.error('Failed to load data')
  } finally {
    campaignLoad.value = false
  }
}

const campaignsTabLoaded = ref(false)
const productsTabLoaded = ref(false)

watch(activeTab, (key) => {
  if (key === 'campaigns' && !campaignsTabLoaded.value) {
    campaignsTabLoaded.value = true
    fetchCampaigns()
  }
  if (key === 'products' && !productsTabLoaded.value) {
    productsTabLoaded.value = true
    fetchProducts()
  }
})

const getProductDetailHref = (record) => {
  const id = record?.productId ?? record?.id
  return id ? `/publisher/products/${id}` : '#'
}

const getProductCurrencySymbol = (record) =>
  record?.currencySymbol ??
  CURRENCY_DATA_MAPPER[record?.currencyCode ?? record?.currency] ??
  currencySymbol.value

const getProductPrice = (record) => {

}

const hasActiveDeal = (record) =>
  Boolean(record?.combineDeal || record?.activeDeal || record?.hasActiveDeal)

const formatPriceOff = (val) => {
  const num = Number(val)
  return Number.isFinite(num) ? `${num.toFixed(1)}% off` : '—'
}

const formatStock = (record) => {
  if (record?.inStock === true || record?.availability === 'IN_STOCK') return 'In Stock'
  if (record?.inStock === false || record?.availability === 'OUT_OF_STOCK') return 'Out of Stock'
  return record?.availability ?? record?.stockStatus ?? '—'
}

const buildProductQueryParams = () => {
  let sortField=''
  let sortDirection=''
  if(productFilter.sort === 'bestSellersAsc'){
    sortField='best_sellers'
    sortDirection='asc'
  }else if(productFilter.sort === 'bestSellersDesc'){
    sortField='best_sellers'
    sortDirection='desc'
  }else if(productFilter.sort === 'priceAsc'){
    sortField='price'
    sortDirection='asc'
  }else if(productFilter.sort === 'priceDesc'){
    sortField='price'
    sortDirection='desc'
  }else if(productFilter.sort === 'newlyAddedAsc'){
    sortField='newly_added'
    sortDirection='asc'
  }else if(productFilter.sort === 'newlyAddedDesc'){
    sortField='newly_added'
    sortDirection='desc'
  }
  let param= {
  programId: programId.value,
  page: productPagination.current,
  size: productPagination.size,
  keyword: productFilter.keyword || undefined,
  activeDeal: productFilter.activeDeals || undefined,
  inStock: productFilter.inStockOnly || undefined,
  sortField: sortField || undefined,
  sortDirection: sortDirection || undefined,
  }
  return param
}

const fetchProducts = async () => {
  if (!programId.value) return
  productLoad.value = true

  try {
    const res = await creatorConnectionsApi.getBonusProgramProducts(buildProductQueryParams())
    if (res?.success && res?.data) {
      const list = res.data.data ?? res.data
      productList.value = Array.isArray(list) ? list : []
      productPagination.total = res.data.total ?? productList.value.length
    } else {
      productList.value = []
      productPagination.total = 0
      if (res?.error) message.error(res.error)
    }
  } catch {
    productList.value = []
    productPagination.total = 0
    message.error('Failed to load data')
  } finally {
    productLoad.value = false
  }
}

const onProductSearch = () => {
  productPagination.current = 1
  fetchProducts()
}

const onProductFilterChange = () => {
  productPagination.current = 1
  fetchProducts()
}

const onProductPageChange = (page) => {
  productPagination.current = page
  fetchProducts()
}

const onProductTableChange = ({ current, pageSize }) => {
  productPagination.current = current
  productPagination.size = pageSize
  productPagination.pageSize = pageSize
  fetchProducts()
}

const onCampaignTableChange = ({ current, pageSize }) => {
  campaignPagination.current = current
  campaignPagination.size = pageSize
  campaignPagination.pageSize = pageSize
  fetchCampaigns()
}

const openBonusRule = () => {
  bonusRuleModalRef.value?.show({
    programId: programId.value,
    id: programId.value,
  })
}
const openProducts = (record) => {
  campaignProductsModalRef.value?.show(record, marketplace.value,programId.value)
}
const accpetAmazonProduct=(record)=>{
  if(record.jumpUrl){
      window.open(record.jumpUrl, '_blank');
    }
}
const openAmazonProduct=async(record)=>{
  if(!record.productId) return
  const res = await publisherProductApi.getProductDetail(record.productId)
  const { success, data } = res
  if(success){
    window.open(data.productUrl, '_blank');
  }
}
onMounted(() => {
  fetchProgramDetail()
})
</script>

<style lang="less" scoped>
.button-box{
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.iconAsc {
  transform: rotate(180deg);
}
.bonus-program-details {
  padding: 0 16px 16px;
  padding-top: 0;
}

.view-content {
  background: #fff;
  border-radius: 8px;
  box-shadow: var(--section-box-shadow-6);
  padding: 16px 20px 24px;
  min-height: 400px;
}

.tabs-wrap {
  max-width: 560px;
  margin-bottom: 20px;
}

:deep(.deal-cell) {
  display: flex;
  // flex-direction: column;
  gap: 8px;
}

.tabs-wrap-tabs {
  margin: 0;
}

.tab-panel {
  min-height: 280px;
}

.metric-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.metric-card {
  width: 200px;
  min-height: 61.5px;
  box-sizing: border-box;
  background-color: var(--bg-gray-8);
  border-radius: 8px;
  padding: 8px 7px 7px 12px;
}

.metric-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: 0.3px;
}

.metric-value {
  font-family: Sora, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: rgba(15, 6, 41, 1);
  letter-spacing: 0.4px;
  margin-top: 4px;
}

.info-panel {
  border: 1px solid #e8e5ed;
  border-radius: 8px;
  padding: 20px 24px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
}

.info-label {
  flex: 0 0 120px;
  font-family: Sora, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(15, 6, 41, 0.65);
}

.info-value {
  flex: 1;
  font-family: Sora, sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: rgba(15, 6, 41, 0.9);
  word-break: break-word;
}

.linkStyle {
  color: #7653da;
  text-decoration: underline;
}

.tab-panel-table {
  padding-top: 4px;
}

.accept-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.not-accepted-link {
  color: #7653da;
  font-weight: 500;
  text-decoration: none;
}

.external-link {
  display: inline-flex;
  color: rgba(15, 6, 41, 0.45);

  .link-icon {
    font-size: 14px;
  }

  &:hover {
    color: #26aadc;
  }
}

.products-toolbar {
  // display: flex;
  // flex-wrap: wrap;
  // align-items: center;
  // justify-content: space-between;
  margin: 16px 0;
}
.products-toolbar-top{
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.mid-filter {
  display: flex;
  align-items: space-between;
  position: relative;
  margin-bottom: 16px;
  margin-top: 16px;
  // justify-content: space-between;
  padding: 16px;
  padding-right: 96px;
  border-radius: 8px;
  background-color: var(--bg-gray-1);
  flex-wrap: wrap;
  gap: 16px; // 垂直和水平间距都是16px
  // overflow: hidden;
  max-height: 86px; // 限制为一行的高度，不包括gap，因为第一行不会有gap
  transition: max-height 0.3s ease;

  &.expanded {
    max-height: none; // 展开时移除高度限制
    overflow: visible;
  }

  &-item {
    display: flex;
    flex-direction: column;
    // width: 20%;

    &.filter-item-hidden {
      display: none !important;
    }
  }

  //  &-item:first-child{
  //  width: 20%;
  //  }
  //  &-item:nth-child(2){
  //   width: 14%;
  //  }
  &-checkbox {
    background-color: var(--bg-gray-6);
    border-radius: 8px;
    height: 32px;
    padding: 0 8px;
    display: flex;
    align-items: center;
    // justify-content: center;
  }

  &-title {
    font-size: 13px;
    font-weight: 600;
    height: 18px;
    // opacity: 0.7;
    letter-spacing: 0.3px;
    line-height: 18px;
    color: #0f0629;
    margin-bottom: 4px;
  }
}

.products-toolbar-right {
  // display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.products-search {
  width: 220px;
}

.products-filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.products-filter-label {
  font-family: Sora, sans-serif;
  font-size: 14px;
  color: var(--dark-2);
  white-space: nowrap;
}

.product-cell {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: inherit;
  text-decoration: none;

  &:hover .product-title {
    color: #7653da;
  }
}

.product-img-wrap {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  background: #f3f2f7;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.product-title {
  font-family: Sora, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: #0f0629;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-asin {
  font-size: 12px;
  color: rgba(15, 6, 41, 0.5);
}

.deal-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #eb4e3d;
  background: #fdeeec;
}
</style>
